import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {Tenant} from 'src/apis/autoGenerate/types/tenant';
import {tenantServiceFind, apartmentServiceFind} from 'src/apis/autoGenerate/services';
import {RootState} from '../store';

interface TenantsState {
  tenantList: Tenant[];
  loading: boolean;
}

const initialState: TenantsState = {
  tenantList: [],
  loading: false,
};

export const fetchTenantStats = createAsyncThunk(
  'tenant/fetchStats',
  async ({orgId, userId}: {orgId: string; userId?: string}) => {
    try {
      // First, get the user's accessible apartments
      const apartmentsRes = await apartmentServiceFind(orgId, undefined, undefined, userId);
      const apartments = apartmentsRes?.data?.apartments || [];

      if (apartments.length === 0) {
        return [];
      }

      // Get apartment IDs
      const apartmentIds = apartments.map(apt => apt.id).filter(Boolean) as string[];

      // Fetch tenants from all accessible apartments
      const tenantPromises = apartmentIds.map(apartmentId =>
        tenantServiceFind(undefined, apartmentId, 1000, 1, orgId)
      );

      const results = await Promise.allSettled(tenantPromises);

      let allTenants: Tenant[] = [];
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value?.data?.tenants) {
          allTenants = [...allTenants, ...result.value.data.tenants];
        }
      });

      // Remove duplicate tenants based on tenant ID
      const uniqueTenants = allTenants.filter(
        (tenant, index, self) => index === self.findIndex(t => t.id === tenant.id)
      );

      return uniqueTenants;
    } catch (error) {
      console.error('Error fetching tenant stats:', error);
      return [];
    }
  }
);

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTenantStats.pending, state => {
        state.loading = true;
      })
      .addCase(fetchTenantStats.fulfilled, (state, action) => {
        state.tenantList = action.payload;
        state.loading = false;
      })
      .addCase(fetchTenantStats.rejected, state => {
        state.loading = false;
      });
  },
});

export const selectTenantStats = (state: RootState) => state.resident.tenantList;
export default tenantSlice.reducer;
